import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, type PantryEntry, type Product } from '../api/endpoints';
import { ApiException } from '../api/client';
import { Skeleton, ErrorState } from '../components/states';
import { track } from '../lib/analytics';

export function PantryScreen() {
  const navigate = useNavigate();
  const [pantry, setPantry] = useState<PantryEntry[] | null>(null);
  const [top, setTop] = useState<Product[]>([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [p, t] = await Promise.all([endpoints.getPantry(), endpoints.getProducts({ limit: 50 })]);
      setPantry(p);
      setTop(t);
    } catch (e) {
      setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить холодильник');
    }
  }, []);

  useEffect(() => {
    // Намеренный fetch-in-effect (план без TanStack): разовый setState на маунте — приемлемо.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  // Поиск с debounce 300 мс.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const q = search.trim();
    if (q === '') {
      // Сброс результатов при пустом запросе — намеренно в эффекте поиска.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResults([]);
      return;
    }
    timer.current = setTimeout(() => {
      endpoints
        .getProducts({ search: q, limit: 30 })
        .then(setResults)
        .catch(() => setResults([]));
    }, 300);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [search]);

  const inPantry = new Set((pantry ?? []).map((e) => e.product?.id));

  async function add(p: Product) {
    if (!p.id || inPantry.has(p.id)) return;
    const entry: PantryEntry = { product: p, addedAt: new Date().toISOString(), source: 'custom' };
    setPantry((cur) => [...(cur ?? []), entry]); // оптимистично
    setNotice(null);
    try {
      await endpoints.addPantryProducts([p.id]);
    } catch (e) {
      setPantry((cur) => (cur ?? []).filter((x) => x.product?.id !== p.id)); // откат
      setNotice(e instanceof ApiException ? e.body.message : 'Не удалось добавить');
    }
  }

  async function remove(id?: string) {
    if (!id) return;
    const prev = pantry ?? [];
    setPantry(prev.filter((x) => x.product?.id !== id)); // оптимистично
    setNotice(null);
    try {
      await endpoints.removePantryProduct(id);
    } catch (e) {
      setPantry(prev); // откат
      setNotice(e instanceof ApiException ? e.body.message : 'Не удалось убрать');
    }
  }

  if (pantry === null && !error) {
    return (
      <div className="space-y-3 px-4 pt-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-4 pt-6">
        <ErrorState message={error} onRetry={() => void load()} />
      </div>
    );
  }

  const quick = top.filter((p) => p.id && !inPantry.has(p.id)).slice(0, 24);

  return (
    // Доп. нижний отступ — чтобы последние чипсы не прятались за липкой кнопкой над навигацией.
    <div className="px-4 pt-6 pb-24">
      <h1 className="mb-1 text-2xl font-bold text-ink">Холодильник</h1>
      <p className="mb-4 text-sm text-ink/60">
        Отмечайте не только из холодильника — крупы, специи и всё из кладовой тоже.
      </p>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Поиск продукта"
        aria-label="Поиск продукта"
        className="mb-4 w-full rounded-xl border border-black/10 px-4 py-3"
      />

      {results.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="search-results">
          {results.map((p) => (
            <button
              key={p.id}
              onClick={() => void add(p)}
              className="rounded-full bg-white px-3 py-1.5 text-sm shadow-sm"
            >
              + {p.nameRu}
            </button>
          ))}
        </div>
      )}

      <h2 className="mb-2 text-sm font-semibold text-ink/60">Сейчас дома</h2>
      {pantry!.length === 0 ? (
        <p className="mb-6 text-ink/60">Пока пусто. Добавьте продукты ниже или через поиск.</p>
      ) : (
        <div className="mb-6 flex flex-wrap gap-2">
          {pantry!.map((e) => (
            <button
              key={e.product?.id}
              onClick={() => void remove(e.product?.id)}
              className="rounded-full bg-primary-50 px-3 py-1.5 text-sm text-primary-700"
            >
              {e.product?.nameRu} ✕
            </button>
          ))}
        </div>
      )}

      <h2 className="mb-2 text-sm font-semibold text-ink/60">Добавить быстро</h2>
      <div className="flex flex-wrap gap-2">
        {quick.map((p) => (
          <button
            key={p.id}
            onClick={() => void add(p)}
            className="rounded-full bg-white px-3 py-1.5 text-sm shadow-sm"
          >
            + {p.nameRu}
          </button>
        ))}
      </div>

      {notice && (
        <p className="mt-4 text-sm text-red-600" role="alert">
          {notice}
        </p>
      )}

      {/* Липкая кнопка над нижней навигацией (z выше навигации, отступ снизу = высота навигации + safe-area). */}
      <div className="pointer-events-none fixed inset-x-0 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-20 px-4">
        <div className="mx-auto max-w-md">
          <button
            onClick={() => {
              track('cook_click');
              navigate('/cook');
            }}
            className="pointer-events-auto w-full rounded-2xl bg-primary-600 px-4 py-3.5 font-semibold text-white shadow-card-hover transition active:scale-[0.99]"
          >
            Что приготовить
          </button>
        </div>
      </div>
    </div>
  );
}
